import * as React from "react"
import { useEditorState } from "@tiptap/react"
import { Button } from "../../tiptap-ui-primitive/button"
import { useTiptapEditor } from "../../hooks/use-tiptap-editor"

type ListType = "bulletList" | "orderedList" | "taskList"

export interface ListDropdownMenuProps {
  types?: ListType[]
  portal?: boolean
}

export const ListDropdownMenu: React.FC<ListDropdownMenuProps> = ({ 
  types = ["bulletList", "orderedList", "taskList"] 
}) => {
  const editor = useTiptapEditor()
  const [isOpen, setIsOpen] = React.useState(false)
  // Сохраняем позицию селекции при открытии
  const savedSelectionRef = React.useRef<{ from: number; to: number } | null>(null)

  const state = useEditorState({
    editor,
    selector: (ctx) => ({
      isBulletList: ctx.editor?.isActive("bulletList") ?? false,
      isOrderedList: ctx.editor?.isActive("orderedList") ?? false,
      isTaskList: ctx.editor?.isActive("taskList") ?? false,
    }),
  })

  const handleOpen = () => {
    // Сохраняем текущую селекцию
    if (editor) {
      const { from, to } = editor.state.selection
      savedSelectionRef.current = { from, to }
    }
    setIsOpen(!isOpen)
  }

  const handleSelect = (type: ListType) => {
    if (!editor) return
    
    // Восстанавливаем селекцию
    if (savedSelectionRef.current) {
      const { from, to } = savedSelectionRef.current
      editor.chain().setTextSelection({ from, to }).run()
    }
    
    if (type === "bulletList") {
      editor.chain().focus().toggleBulletList().run()
    } else if (type === "orderedList") {
      editor.chain().focus().toggleOrderedList().run()
    } else if (type === "taskList") {
      editor.chain().focus().toggleTaskList().run()
    }
    setIsOpen(false)
    savedSelectionRef.current = null
  }

  const labels: Record<ListType, string> = {
    bulletList: "Маркированный список",
    orderedList: "Нумерованный список",
    taskList: "Список задач",
  }

  return (
    <div style={{ position: "relative" }}>
      <Button onClick={handleOpen} aria-label="Списки">
        Список
        <span style={{ marginLeft: 4 }}>▼</span>
      </Button>
      {isOpen && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          background: "var(--tt-bg-color, white)",
          border: "1px solid var(--tt-border-color, #e5e5e5)",
          borderRadius: 4,
          zIndex: 100,
          minWidth: 120,
        }}>
          {types.map((type) => (
            <Button 
              key={type} 
              onClick={() => handleSelect(type)}
              data-active={
                (type === "bulletList" && state.isBulletList) ||
                (type === "orderedList" && state.isOrderedList) ||
                (type === "taskList" && state.isTaskList)
              }
              style={{ width: "100%", justifyContent: "flex-start" }}
            >
              {labels[type]}
            </Button>
          ))}
        </div>
      )}
    </div>
  )
}
